import {
  checklistActionKey,
  type ChecklistToSave,
  removeFlushedChecklistActions,
} from "src/redux/slices/entrySlice";
import type { AppDispatch } from "src/redux/store";
import { store } from "src/redux/store";

type QueuedChecklistAction = ChecklistToSave;

type PostFn = (args: {
  request: string;
  body: { params: Record<string, any> };
}) => Promise<any>;

// useSaveEntry and useAutoSave both flush, each from its own render closure,
// so a save landing mid-flush would re-post the same actions.
let inFlight: Promise<void> | null = null;

const flush = async (
  post: PostFn,
  entryId: string,
  queue: QueuedChecklistAction[],
  dispatch: AppDispatch,
) => {
  // `rid` must not be overwritten with whichever entry is saved next, so an
  // action belonging to another entry stays queued.
  const mine = queue.filter(entry => !entry.rid || entry.rid === entryId);
  if (mine.length === 0) return;

  const flushed: string[] = [];

  for (const entry of mine) {
    const params: Record<string, any> = {
      cid: entry.cid,
      rid: entryId,
      lbid: entry.lbid,
      yachtid: entry.yachtid,
    };
    if (entry.item) params.item = entry.item;
    if (entry.action) params.action = entry.action;

    // `post` swallows errors and resolves undefined, so a falsy result is the
    // only signal that this action did not land.
    const result = await post({
      request: entry.url,
      body: {
        params,
        ...(entry.lbname ? { _lbname: entry.lbname } : {}),
      },
    });
    if (result) flushed.push(checklistActionKey(entry));
  }

  if (flushed.length > 0) {
    dispatch(removeFlushedChecklistActions(flushed));
  }
};

export const readChecklistQueue = () =>
  store.getState().entrySlice.newChecklistToSave;

export async function flushStoredChecklistQueue(
  post: PostFn,
  entryId: string,
  dispatch: AppDispatch,
) {
  return flushChecklistQueue(post, entryId, readChecklistQueue(), dispatch);
}

export async function flushChecklistQueue(
  post: PostFn,
  entryId: string,
  queue: QueuedChecklistAction[],
  dispatch: AppDispatch,
) {
  if (!entryId || queue.length === 0) return;

  if (inFlight) {
    await inFlight;
    return;
  }

  inFlight = flush(post, entryId, queue, dispatch).finally(() => {
    inFlight = null;
  });

  await inFlight;
}
